import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { HOME_TRANSLATIONS } from '../home/home-translations';
import { environment } from 'src/environments/environment';
import { isRegistrationClosed, isAgencyRegistrationClosed } from 'src/app/utils/registration-cutoff.util';
import { getHeaderMenuConfig, HeaderMenuConfig } from 'src/app/utils/header-menu-config.util';
import { SiteConfigService } from 'src/app/core/services/site-config.service';

@Component({
  selector: 'app-public-header',
  imports: [RouterModule, CommonModule],
  templateUrl: './public-header.html',
  styleUrl: './public-header.css'
})
export class PublicHeader implements OnInit, OnDestroy {
  private router = inject(Router);
  private siteConfigService = inject(SiteConfigService);
  //private launchService = inject(SiteLaunchService);
  private sanitizer = inject(DomSanitizer);
  private http = inject(HttpClient);
  readonly isProduction = false;

  get isRegistrationClosed(): boolean {
    return isRegistrationClosed();
  }

  get isAgencyRegistrationClosed(): boolean {
    return isAgencyRegistrationClosed();
  }
  menuConfig: HeaderMenuConfig = getHeaderMenuConfig();
  private menuConfigListener?: (e: Event) => void;
  mobileNavOpen = false;
  fontSize = 100; // percent
  currentTheme: 'light' | 'dark' = 'light';
  currentLang: 'en' | 'or' = 'or';

  // Access Code Modal
  showAccessCodeModal = false;
  enteredAccessCode = '';
  correctAccessCode = '';
  accessCodeError = false;

  // Tutorial Modal Logic
  showTutorialModal = false;
  selectedTutorialVideoUrl: SafeResourceUrl | null = null;
  activeVideoId = '';

  tutorials = [
    {
      id: 'ct',
      url: 'https://www.youtube.com/embed/MHt8PeVS3_E?rel=0',
      descEn: 'Cultural Troupe Owner and Members Registration Tutorial',
      descOr: 'ସାଂସ୍କୃତିକ ଦଳର ମୁଖ୍ୟ ଏବଂ ସଦସ୍ୟଙ୍କ ପଞ୍ଜୀକରଣ'
    },
    {
      id: 'ra',
      url: 'https://www.youtube.com/embed/UfdnM5FJCIk?rel=0',
      descEn: 'Requesting Agency (RA) Registration Tutorial',
      descOr: 'ଆର୍. ଏ ପଞ୍ଜୀକରଣ'
    }
  ];

  openTutorialModal() {
    this.showTutorialModal = true;
    this.selectTutorialVideo(this.tutorials[0]);
  }

  closeTutorialModal() {
    this.showTutorialModal = false;
    this.selectedTutorialVideoUrl = null;
    this.activeVideoId = '';
  }

  selectTutorialVideo(video: typeof this.tutorials[0]) {
    this.activeVideoId = video.id;
    this.selectedTutorialVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(video.url);
  }

  get isEnglishActive() { return this.currentLang === 'en'; }
  get isOdiaActive() { return this.currentLang === 'or'; }
  get isLoginPage() { return this.router.url === '/login' || this.router.url.startsWith('/login/'); }

  get t() { return (HOME_TRANSLATIONS as any)[this.currentLang]; }

  // Guidelines accordion open/closed state
  glOpen: Record<string, boolean> = {
    definitions: true,
    registration: true,
    programmes: true,
    artforms: true
  };
  toggleGl(key: string) { this.glOpen[key] = !this.glOpen[key]; }

  guidelinesData: any[] = [];

  constructor() {
    this.initLanguage();
  }
  ngOnInit(): void {
    this.initLanguage();
    // this.checkAccessCode();
    this.initMenuConfig();
    this.loadGuidelines();
  }

  loadGuidelines(): void {
    this.http.get<any[]>('assets/data/guidelines.json').subscribe({
      next: (data) => {
        this.guidelinesData = data;
        // Initialize accordion state for dynamically loaded sections
        data.forEach(section => {
          if (this.glOpen[section.id] === undefined) {
            this.glOpen[section.id] = true;
          }
        });
      },
      error: (err) => console.error('Error loading guidelines:', err)
    });
  }

  initMenuConfig(): void {
    this.siteConfigService.getSiteConfig().subscribe();
    this.menuConfig = getHeaderMenuConfig();
    if (typeof window !== 'undefined') {
      this.menuConfigListener = (e: any) => {
        if (e?.detail) {
          this.menuConfig = e.detail;
        } else {
          this.menuConfig = getHeaderMenuConfig();
        }
      };
      window.addEventListener('headerMenuConfigChange', this.menuConfigListener);
    }
  }

  /** Gate only on the hosted dev site — not localhost and not production. */
  private shouldPromptAccessCode(): boolean {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname.toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1') return false;
    if (host === 'amakalakara.odisha.gov.in' || host.endsWith('.odisha.gov.in')) {
      return false;
    }
    if (
      host === 'amakalakara.nimbletech.co.in' ||
      host.endsWith('.amakalakara.nimbletech.co.in') ||
      host === 'dev.amakalakara.com' ||
      host.endsWith('.dev.amakalakara.com')
    ) {
      return true;
    }
    return !!(environment as { requireAccessCode?: boolean }).requireAccessCode;
  }

  checkAccessCode(): void {
    if (!this.shouldPromptAccessCode()) {
      this.showAccessCodeModal = false;
      return;
    }
    this.http.get<any>(`assets/launch-config.json?v=${new Date().getTime()}`).subscribe({
      next: (config: any) => {
        if (config && config.accessCode) {
          this.correctAccessCode = config.accessCode;
          const grantedCode = sessionStorage.getItem('site_access_granted_code');
          if (grantedCode === this.correctAccessCode) {
            this.showAccessCodeModal = false;
          } else {
            sessionStorage.removeItem('site_access_granted_code');
            this.showAccessCodeModal = true;
          }
        } else {
          sessionStorage.removeItem('site_access_granted_code');
          this.showAccessCodeModal = false;
        }
      },
      error: () => {
        this.showAccessCodeModal = false;
      }
    });
  }

  submitAccessCode(): void {
    if (this.enteredAccessCode.trim() === this.correctAccessCode) {
      sessionStorage.setItem('site_access_granted_code', this.correctAccessCode);
      this.showAccessCodeModal = false;
      this.accessCodeError = false;
    } else {
      this.accessCodeError = true;
    }
  }

  onAccessCodeInput(event: Event): void {
    this.enteredAccessCode = (event.target as HTMLInputElement).value;
  }

  private initLanguage() {
    const savedLang = localStorage.getItem('app_lang');
    const lang: 'en' | 'or' = savedLang === 'en' ? 'en' : 'or';
    this.currentLang = lang;
    localStorage.setItem('app_lang', lang);
    this.updateBodyClass(lang);

    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('langChange', { detail: lang }));
    });
  }

  setLanguage(lang: 'en' | 'or') {
    // Remove early return to force sync across all components in case of desync
    this.currentLang = lang;
    localStorage.setItem('app_lang', lang);
    this.updateBodyClass(lang);
    window.dispatchEvent(new CustomEvent('langChange', { detail: lang }));
  }

  private updateBodyClass(lang: string) {
    document.documentElement.lang = lang;
    if (lang === 'or') {
      document.body.classList.add('lang-or');
    } else {
      document.body.classList.remove('lang-or');
    }
  }

  toggleLanguage() {
    const nextLang = this.currentLang === 'en' ? 'or' : 'en';
    this.setLanguage(nextLang);
  }

  changeFontSize(action: 'increase' | 'decrease' | 'reset'): void {
    if (action === 'increase' && this.fontSize < 120) this.fontSize += 10;
    if (action === 'decrease' && this.fontSize > 70) this.fontSize -= 10;
    if (action === 'reset') this.fontSize = 100;
    document.documentElement.style.fontSize = `${this.fontSize}%`;
  }

  setTheme(theme: 'light' | 'dark'): void {
    this.currentTheme = theme;
    document.body.classList.remove('theme-light', 'theme-dark');
    document.body.classList.add(`theme-${theme}`);
    localStorage.setItem('pref_theme', theme);
  }

  isActive(routes: string[]): boolean {
    return routes.some(r => this.router.isActive(r, { paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' }));
  }

  toggleMobileNav(): void {
    this.mobileNavOpen = !this.mobileNavOpen;
    this.setBodyScrollLock(this.mobileNavOpen);
    if (this.mobileNavOpen) {
      // Move focus into the nav after it opens
      setTimeout(() => {
        const closeBtn = document.querySelector<HTMLElement>('.close-mobile-nav');
        closeBtn?.focus();
      }, 50);
    }
  }

  closeMobileNav(): void {
    this.mobileNavOpen = false;
    this.setBodyScrollLock(false);
    // Return focus to the hamburger button that opened the drawer
    const hamburger = document.querySelector<HTMLElement>('.hamburger');
    hamburger?.focus();
  }

  /** The drawer can be torn down without closeMobileNav() ever running —
   *  a browser/hardware back press navigates away and destroys this
   *  component directly. Without this the body keeps mobile-nav-lock and the
   *  entire site stays unscrollable until a reload. */
  ngOnDestroy(): void {
    this.setBodyScrollLock(false);
    if (typeof window !== 'undefined' && this.menuConfigListener) {
      window.removeEventListener('headerMenuConfigChange', this.menuConfigListener);
    }
  }

  /** Freezes the page behind the drawer. Without this a swipe over the menu
   *  scrolls the page instead, which on mobile also collapses the URL bar and
   *  makes the drawer appear to shift. */
  private setBodyScrollLock(locked: boolean): void {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('mobile-nav-lock', locked);
  }

  // Escape key closes mobile nav
  onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.mobileNavOpen) {
      this.closeMobileNav();
    }
  }

  closeDropdown(e: Event): void {
    const drop = (e.currentTarget as HTMLElement).closest<HTMLElement>('.nav-dropdown');
    if (!drop) return;
    setTimeout(() => {
      drop.classList.add('closing');
      (document.activeElement as HTMLElement)?.blur();

      // Automatically remove the closing class after a short delay
      // so it doesn't get permanently stuck if the user doesn't mouseleave.
      setTimeout(() => {
        drop.classList.remove('closing');
      }, 400);
    }, 150);
  }

  onTriggerClick(e: Event): void {
    const drop = (e.currentTarget as HTMLElement).closest<HTMLElement>('.nav-dropdown');
    if (drop) {
      drop.classList.remove('closing');
    }
  }

  forceNavigate(path: string, role: string, e: Event): void {
    e.preventDefault();
    e.stopPropagation();
    this.closeDropdown(e);
    this.closeMobileNav();
    this.router.navigate([path], { queryParams: { role } }).catch(err => {
      console.error('Navigation error:', err);
    });
  }

  navigateToSection(sectionId: string, e: Event): void {
    e.preventDefault();
    this.closeMobileNav();

    const scrollToElement = () => {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return true;
      }
      return false;
    };

    setTimeout(() => {
      if (!scrollToElement()) {
        // Element not on current page — navigate to home, then scroll after render
        this.router.navigate(['/'], { fragment: sectionId }).then(() => {
          // Wait for home page content to render before scrolling
          let attempts = 0;
          const poll = setInterval(() => {
            if (scrollToElement() || ++attempts > 20) clearInterval(poll);
          }, 100);
        });
      }
    }, 150);

    this.closeDropdown(e);
  }

  openGuidelinesMobile(): void {
    // Close mobile nav first, then open offcanvas after animation completes
    this.closeMobileNav();
    setTimeout(() => {
      const el = document.getElementById('guidelinesOffcanvas');
      if (el) {
        const { Offcanvas } = (window as any).bootstrap;
        const offcanvas = Offcanvas.getOrCreateInstance(el);
        offcanvas.show();
      }
    }, 320);
  }

  onLoginClick(event: Event): void {
    this.closeMobileNav();
  }

  onRegistrationClick(event: Event): void {
    this.closeMobileNav();
  }

  //mode: string = localStorage.getItem('mode') || "dev";

  // toggleApiUrl(): void {
  //   if (this.mode == "dev") {
  //     this.mode = "prod";
  //     environment.apiUrl = "https://amakalakara.odisha.gov.in:8443";
  //   } else {
  //     this.mode = "dev";
  //     environment.apiUrl = "https://api-dev.amakalakara.com";
  //   }
  //   localStorage.setItem('mode', this.mode);
  //   window.location.reload();
  // }
}
